/**
 * Collection Detail Page
 *
 * View and edit a single collection, including managing artworks.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  ArrowLeft,
  Save,
  Loader2,
  Image as ImageIcon,
  Plus,
  GripVertical,
  Trash2,
  X,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import {
  useCollection,
  useUpdateCollection,
  useCreateCollection,
  useRemoveArtworkFromCollection,
  useAddArtworkToCollection,
  useReorderCollectionArtworks,
  useMyArtworks,
} from '../hooks'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { EaselImageField } from '../components/EaselImageField'
import type { CreateCollectionData, CollectionArtwork } from '../types'

// =============================================================================
// Form Schema
// =============================================================================

const collectionSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  slug: z
    .string()
    .max(100)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Invalid slug format')
    .optional()
    .or(z.literal('')),
  subtitle: z.string().max(100).optional(),
  description: z.string().max(2000).optional(),
  featuredImage: z.string().url().optional().or(z.literal('')),
  tags: z.array(z.string()).max(20).optional(),
  featured: z.boolean().optional(),
  visible: z.boolean().optional(),
})

type CollectionFormData = z.infer<typeof collectionSchema>

// =============================================================================
// Artwork Item Component
// =============================================================================

interface ArtworkItemProps {
  artwork: CollectionArtwork
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  isFirst: boolean
  isLast: boolean
  disabled?: boolean
}

function ArtworkItem({
  artwork,
  onRemove,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  disabled,
}: ArtworkItemProps) {
  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg group">
      <div className="flex flex-col items-center text-muted-foreground">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onMoveUp}
          disabled={isFirst || disabled}
          aria-label="Move up"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <GripVertical className="h-4 w-4 opacity-40" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onMoveDown}
          disabled={isLast || disabled}
          aria-label="Move down"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
      <div className="w-16 h-16 bg-muted rounded flex-shrink-0 overflow-hidden">
        <img
          src={artworkApi.getArtworkImageUrl(artwork.artworkId)}
          alt={artwork.artwork?.name || `Artwork #${artwork.artworkId}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{artwork.artwork?.name || `Artwork #${artwork.artworkId}`}</p>
        <p className="text-sm text-muted-foreground">ID: {artwork.artworkId}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={onRemove}
        disabled={disabled}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  )
}

// =============================================================================
// Add Artworks Dialog
// =============================================================================

interface AddArtworksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  collectionId: number
  existingArtworkIds: number[]
  nextDisplayOrder: number
}

function AddArtworksDialog({
  open,
  onOpenChange,
  collectionId,
  existingArtworkIds,
  nextDisplayOrder,
}: AddArtworksDialogProps) {
  const { data: artworks, isLoading } = useMyArtworks(open)
  const addArtwork = useAddArtworkToCollection()
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  // Reset selection whenever the dialog is (re)opened
  useEffect(() => {
    if (open) setSelectedIds([])
  }, [open])

  const available = (artworks || []).filter(
    (a) => !existingArtworkIds.includes(a.id)
  )

  const toggle = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleConfirm = async () => {
    // Add sequentially so displayOrder stays deterministic
    for (let i = 0; i < selectedIds.length; i++) {
      await addArtwork.mutateAsync({
        collectionId,
        artworkId: selectedIds[i],
        displayOrder: nextDisplayOrder + i,
      })
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Artworks</DialogTitle>
          <DialogDescription>
            Select artworks to add to this collection.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[420px] overflow-y-auto -mx-2 px-2">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : available.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {(artworks || []).length === 0
                ? 'You have no artworks yet.'
                : 'All of your artworks are already in this collection.'}
            </p>
          ) : (
            <div className="space-y-2">
              {available.map((art) => {
                const checked = selectedIds.includes(art.id)
                return (
                  <button
                    type="button"
                    key={art.id}
                    onClick={() => toggle(art.id)}
                    className="w-full flex items-center gap-3 p-2 border rounded-lg text-left hover:bg-accent transition-colors"
                  >
                    {/* Presentational only — the row button owns the toggle. Wiring
                        onCheckedChange here too fired toggle twice per click (net no-op). */}
                    <Checkbox checked={checked} className="pointer-events-none" tabIndex={-1} />
                    <div className="w-12 h-12 bg-muted rounded flex-shrink-0 overflow-hidden">
                      <img
                        src={artworkApi.getArtworkImageUrl(art.id)}
                        alt={art.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{art.name || `Artwork #${art.id}`}</p>
                      <p className="text-xs text-muted-foreground">ID: {art.id}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || addArtwork.isPending}
          >
            {addArtwork.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              `Add ${selectedIds.length || ''} ${selectedIds.length === 1 ? 'Artwork' : 'Artworks'}`.trim()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Tag Input Component
// =============================================================================

interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
}

function TagInput({ value, onChange }: TagInputProps) {
  const [input, setInput] = useState('')

  const addTag = () => {
    const tag = input.trim().toLowerCase()
    if (tag && !value.includes(tag) && value.length < 20) {
      onChange([...value, tag])
      setInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((t) => t !== tagToRemove))
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Add a tag..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addTag()
            }
          }}
        />
        <Button type="button" variant="outline" onClick={addTag}>
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="pr-1">
              {tag}
              <button
                type="button"
                className="ml-1 hover:text-destructive"
                onClick={() => removeTag(tag)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function CollectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const { data: collection, isLoading } = useCollection(isNew ? 0 : parseInt(id || '0'))
  const updateCollection = useUpdateCollection()
  const createCollection = useCreateCollection()
  const removeArtwork = useRemoveArtworkFromCollection()
  const reorderArtworks = useReorderCollectionArtworks()
  const [addArtworksOpen, setAddArtworksOpen] = useState(false)

  const form = useForm<CollectionFormData>({
    resolver: zodResolver(collectionSchema),
    defaultValues: {
      title: '',
      slug: '',
      subtitle: '',
      description: '',
      featuredImage: '',
      tags: [],
      featured: false,
      visible: true,
    },
  })

  // Populate form when collection loads
  useEffect(() => {
    if (collection && !isNew) {
      form.reset({
        title: collection.title,
        slug: collection.slug,
        subtitle: collection.subtitle || '',
        description: collection.description || '',
        featuredImage: collection.featuredImage || '',
        tags: collection.tags || [],
        featured: collection.featured,
        visible: collection.visible,
      })
    }
  }, [collection, isNew, form])

  const onSubmit = async (data: CollectionFormData) => {
    try {
      const payload: CreateCollectionData = {
        ...data,
        slug: data.slug || undefined,
        featuredImage: data.featuredImage || undefined,
      }

      if (isNew) {
        const newCollection = await createCollection.mutateAsync(payload)
        navigate(`/app/easel/collections/${newCollection.id}`)
      } else {
        await updateCollection.mutateAsync({ id: parseInt(id!), data: payload })
      }
    } catch (error) {
      // Error handled by mutation
    }
  }

  const handleRemoveArtwork = async (artworkId: number) => {
    if (collection) {
      await removeArtwork.mutateAsync({
        collectionId: collection.id,
        artworkId,
      })
    }
  }

  // Sorted artworks (by displayOrder) for stable ordering + reorder controls
  const sortedArtworks = [...(collection?.artworks || [])].sort(
    (a, b) => a.displayOrder - b.displayOrder
  )

  const handleMove = async (index: number, direction: -1 | 1) => {
    if (!collection) return
    const target = index + direction
    if (target < 0 || target >= sortedArtworks.length) return
    const reordered = [...sortedArtworks]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)
    await reorderArtworks.mutateAsync({
      collectionId: collection.id,
      artworkIds: reordered.map((a) => a.artworkId),
    })
  }

  if (isLoading && !isNew) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel/collections')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {isNew ? 'New Collection' : collection?.title || 'Collection'}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? 'Create a new collection' : 'Edit collection details and artworks'}
            </p>
          </div>
        </div>
        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={updateCollection.isPending || createCollection.isPending}
        >
          {(updateCollection.isPending || createCollection.isPending) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              {isNew ? 'Create' : 'Save Changes'}
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          {!isNew && <TabsTrigger value="artworks">Artworks ({collection?.artworks?.length || 0})</TabsTrigger>}
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details">
          <Form {...form}>
            <form className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-3">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Title</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Collection title" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="slug"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>URL Slug</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="collection-slug" />
                            </FormControl>
                            <FormDescription>
                              Leave empty to auto-generate from title
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="subtitle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subtitle</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Optional subtitle" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                placeholder="Describe your collection..."
                                rows={4}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tags"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Tags</FormLabel>
                            <FormControl>
                              <TagInput
                                value={field.value || []}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormDescription>
                              Add up to 20 tags to help categorize this collection
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Cover Image</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <FormField
                        control={form.control}
                        name="featuredImage"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <EaselImageField
                                kind="collection"
                                aspectHint="16:9"
                                value={field.value}
                                onChange={(url) => field.onChange(url)}
                              />
                            </FormControl>
                            <FormDescription>
                              Upload a file, pick one of your artworks, or paste a URL
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Visibility</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <FormField
                        control={form.control}
                        name="visible"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between">
                            <div>
                              <FormLabel>Visible</FormLabel>
                              <FormDescription>
                                Show on your portfolio
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="featured"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between">
                            <div>
                              <FormLabel>Featured</FormLabel>
                              <FormDescription>
                                Highlight on homepage
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                  </Card>

                  {!isNew && collection && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Statistics</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Artworks</span>
                          <span className="font-medium">{collection.artworkCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Created</span>
                          <span className="font-medium">
                            {new Date(collection.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Updated</span>
                          <span className="font-medium">
                            {new Date(collection.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </TabsContent>

        {/* Artworks Tab */}
        {!isNew && (
          <TabsContent value="artworks">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Collection Artworks</CardTitle>
                  <CardDescription>
                    Drag to reorder. Artworks will appear in this order on your portfolio.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={() => setAddArtworksOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Artworks
                </Button>
              </CardHeader>
              <CardContent>
                {sortedArtworks.length > 0 ? (
                  <div className="space-y-2">
                    {sortedArtworks.map((artwork, index) => (
                      <ArtworkItem
                        key={artwork.artworkId}
                        artwork={artwork}
                        onRemove={() => handleRemoveArtwork(artwork.artworkId)}
                        onMoveUp={() => handleMove(index, -1)}
                        onMoveDown={() => handleMove(index, 1)}
                        isFirst={index === 0}
                        isLast={index === sortedArtworks.length - 1}
                        disabled={reorderArtworks.isPending || removeArtwork.isPending}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="font-medium mb-2">No artworks yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Add artworks to this collection to display them together
                    </p>
                    <Button variant="outline" onClick={() => setAddArtworksOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Artworks
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {collection && (
        <AddArtworksDialog
          open={addArtworksOpen}
          onOpenChange={setAddArtworksOpen}
          collectionId={collection.id}
          existingArtworkIds={sortedArtworks.map((a) => a.artworkId)}
          nextDisplayOrder={sortedArtworks.length}
        />
      )}
    </div>
  )
}
